import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import QrModal from "@/components/QrModal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  ClipboardText, ForkKnife, Plus, ArrowRight, Eye, Trash, ChartBar, Copy, ChatCircleDots, QrCode
} from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ forms: 0, menus: 0, submissions: 0, orders: 0 });
  const [forms, setForms] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("forms");
  const [qr, setQr] = useState(null); // {url, title, color}

  const load = async () => {
    try {
      const [s, f, m] = await Promise.all([
        api.get("/stats"),
        api.get("/forms"),
        api.get("/menus"),
      ]);
      setStats(s.data);
      setForms(f.data);
      setMenus(m.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteForm = async (id) => {
    if (!window.confirm("Hapus formulir ini? Semua respons juga dihapus.")) return;
    try {
      await api.delete(`/forms/${id}`);
      toast.success("Formulir dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteMenu = async (id) => {
    if (!window.confirm("Hapus menu ini? Semua pesanan juga dihapus.")) return;
    try {
      await api.delete(`/menus/${id}`);
      toast.success("Menu dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(url);
    toast.success("Tautan disalin!");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="nb-badge">Dashboard</span>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-3">
              Halo, {user?.name?.split(" ")[0] || "Pemilik"} 👋
            </h1>
            <p className="text-[#4A4A4A] mt-1">Kelola formulir & menu yang terhubung ke WhatsApp Anda.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/forms/new" className="nb-btn nb-btn-primary" data-testid="dashboard-new-form">
              <Plus size={18} weight="bold" /> Formulir Baru
            </Link>
            <Link to="/menus/new" className="nb-btn nb-btn-secondary" data-testid="dashboard-new-menu">
              <Plus size={18} weight="bold" /> Menu Baru
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <StatCard label="Formulir" value={stats.forms} icon={<ClipboardText size={22} weight="bold" />} color="#A7E4C0" testId="stat-forms" />
          <StatCard label="Menu" value={stats.menus} icon={<ForkKnife size={22} weight="bold" />} color="#FFD166" testId="stat-menus" />
          <StatCard label="Respons" value={stats.submissions} icon={<ChartBar size={22} weight="bold" />} color="#CDB4DB" testId="stat-submissions" />
          <StatCard label="Pesanan" value={stats.orders} icon={<ChatCircleDots size={22} weight="bold" />} color="#fff" testId="stat-orders" />
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-2">
          <button onClick={() => setTab("forms")}
            className={`nb-btn ${tab === "forms" ? "nb-btn-ink" : ""}`}
            data-testid="tab-forms">
            <ClipboardText size={18} weight="bold" /> Formulir ({forms.length})
          </button>
          <button onClick={() => setTab("menus")}
            className={`nb-btn ${tab === "menus" ? "nb-btn-ink" : ""}`}
            data-testid="tab-menus">
            <ForkKnife size={18} weight="bold" /> Menu Dine-in ({menus.length})
          </button>
        </div>

        {loading ? (
          <p className="mt-8 font-display text-2xl">Memuat…</p>
        ) : tab === "forms" ? (
          <ListGrid
            items={forms}
            emptyTitle="Belum ada formulir"
            emptyDesc="Buat formulir pertama Anda. Reservasi, survei, atau pemesanan via WhatsApp."
            emptyCta={<Link to="/forms/new" className="nb-btn nb-btn-primary mt-4" data-testid="empty-create-form"><Plus size={18} weight="bold" /> Buat Formulir</Link>}
            renderItem={(f) => (
              <div key={f.id} className="nb-card p-6 flex flex-col" data-testid={`form-card-${f.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-display font-bold text-xl">{f.title}</h3>
                    <p className="text-sm text-[#4A4A4A] mt-1 line-clamp-2">{f.description || "—"}</p>
                  </div>
                  <span className="nb-badge" style={{ background: f.active ? "#A7E4C0" : "#eee" }}>
                    {f.active ? "Aktif" : "Nonaktif"}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-3 text-xs text-[#4A4A4A]">
                  <span className="mono">{f.fields?.length || 0} field</span>
                  <span>·</span>
                  <span>{f.submission_count || 0} respons</span>
                </div>
                <div className="mono text-xs bg-[#FDFBF7] border-2 border-[#121212] rounded-md px-2 py-1 mt-3 truncate">
                  /f/{f.slug}
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <Link to={`/forms/${f.id}`} className="nb-btn flex-1 justify-center" data-testid={`form-edit-${f.id}`}>Edit</Link>
                  <Link to={`/forms/${f.id}/submissions`} className="nb-btn" data-testid={`form-subs-${f.id}`}><ChartBar size={16} weight="bold" /></Link>
                  <button onClick={() => setQr({ url: `${window.location.origin}/f/${f.slug}`, title: f.title, color: "#A7E4C0" })} className="nb-btn" data-testid={`form-qr-${f.id}`}><QrCode size={16} weight="bold" /></button>
                  <button onClick={() => copyLink(`/f/${f.slug}`)} className="nb-btn" data-testid={`form-copy-${f.id}`}><Copy size={16} weight="bold" /></button>
                  <a href={`/f/${f.slug}`} target="_blank" rel="noreferrer" className="nb-btn" data-testid={`form-preview-${f.id}`}><Eye size={16} weight="bold" /></a>
                  <button onClick={() => deleteForm(f.id)} className="nb-btn" data-testid={`form-delete-${f.id}`}><Trash size={16} weight="bold" /></button>
                </div>
              </div>
            )}
          />
        ) : (
          <ListGrid
            items={menus}
            emptyTitle="Belum ada menu Dine-in"
            emptyDesc="Buat menu digital - pelanggan scan QR di meja, pesanan masuk ke WA Anda."
            emptyCta={<Link to="/menus/new" className="nb-btn nb-btn-secondary mt-4" data-testid="empty-create-menu"><Plus size={18} weight="bold" /> Buat Menu</Link>}
            renderItem={(m) => (
              <div key={m.id} className="nb-card p-6 flex flex-col" data-testid={`menu-card-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-display font-bold text-xl">{m.name}</h3>
                    <p className="text-sm text-[#4A4A4A] mt-1 line-clamp-2">{m.description || "—"}</p>
                  </div>
                  <span className="nb-badge" style={{ background: m.active ? "#FFD166" : "#eee" }}>
                    {m.active ? "Aktif" : "Nonaktif"}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-3 text-xs text-[#4A4A4A]">
                  <span className="mono">{m.items?.length || 0} item</span>
                  <span>·</span>
                  <span>{m.order_count || 0} pesanan</span>
                </div>
                <div className="mono text-xs bg-[#FDFBF7] border-2 border-[#121212] rounded-md px-2 py-1 mt-3 truncate">
                  /m/{m.slug}
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <Link to={`/menus/${m.id}`} className="nb-btn flex-1 justify-center" data-testid={`menu-edit-${m.id}`}>Edit</Link>
                  <Link to={`/menus/${m.id}/orders`} className="nb-btn" data-testid={`menu-orders-${m.id}`}><ChartBar size={16} weight="bold" /></Link>
                  <button onClick={() => setQr({ url: `${window.location.origin}/m/${m.slug}`, title: m.name, color: "#FFD166" })} className="nb-btn" data-testid={`menu-qr-${m.id}`}><QrCode size={16} weight="bold" /></button>
                  <button onClick={() => copyLink(`/m/${m.slug}`)} className="nb-btn" data-testid={`menu-copy-${m.id}`}><Copy size={16} weight="bold" /></button>
                  <a href={`/m/${m.slug}`} target="_blank" rel="noreferrer" className="nb-btn" data-testid={`menu-preview-${m.id}`}><Eye size={16} weight="bold" /></a>
                  <button onClick={() => deleteMenu(m.id)} className="nb-btn" data-testid={`menu-delete-${m.id}`}><Trash size={16} weight="bold" /></button>
                </div>
              </div>
            )}
          />
        )}
      </main>
      <QrModal open={!!qr} onClose={() => setQr(null)} url={qr?.url} title={qr?.title || ""} color={qr?.color} />
    </div>
  );
}

function StatCard({ label, value, icon, color, testId }) {
  return (
    <div className="nb-card p-5" data-testid={testId}>
      <div className="w-10 h-10 border-2 border-[#121212] rounded-lg flex items-center justify-center" style={{ background: color }}>
        {icon}
      </div>
      <div className="font-display font-black text-3xl mt-3">{value}</div>
      <div className="text-xs uppercase tracking-widest font-bold text-[#4A4A4A] mt-1">{label}</div>
    </div>
  );
}

function ListGrid({ items, emptyTitle, emptyDesc, emptyCta, renderItem }) {
  if (!items || items.length === 0) {
    return (
      <div className="nb-card mt-6 p-10 text-center">
        <h3 className="font-display font-black text-2xl">{emptyTitle}</h3>
        <p className="text-[#4A4A4A] mt-2 max-w-md mx-auto">{emptyDesc}</p>
        <div className="flex justify-center">{emptyCta}</div>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
      {items.map(renderItem)}
    </div>
  );
}
